import { useState, useMemo, type FormEvent } from 'react'
import { CheckCircle2, XCircle, Copy, Check } from 'lucide-react'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import PasswordInput from '@/components/auth/PasswordInput'
import { useAuthStore } from '@/store/useAuthStore'

import AccountSettingsIcon from '@/assets/accountsettings.svg'

const AccountSettings = () => {
    const { user } = useAuthStore()
    const [activeTab, setActiveTab] = useState<'account' | 'security'>(
        'account'
    )
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false)
    const [copiedField, setCopiedField] = useState<string | null>(null)

    const handleCopy = (text: string, field: string) => {
        navigator.clipboard.writeText(text)
        setCopiedField(field)
        setTimeout(() => setCopiedField(null), 2000)
    }

    const displayName = user?.fullName ?? 'N/A'
    const displayEmail = user?.email ?? 'N/A'
    const displayId = user?.id ?? 'N/A'
    const displayRole = user?.role ?? 'N/A'

    const getInitials = (name: string) => {
        if (name === 'N/A') return 'NA'
        return name
            .split(' ')
            .map(n => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2)
    }

    return (
        <div className="space-y-6">
            {/* Header Section */}
            <div className="flex items-start gap-4">
                <div className="space-y-2">
                    <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                            <AvatarImage src="" alt={displayName} />
                            <AvatarFallback className="bg-[#FFF3E0] text-[#FF9800] text-sm font-medium">
                                {getInitials(displayName)}
                            </AvatarFallback>
                        </Avatar>
                        <h1 className="text-2xl font-semibold text-foreground">
                            {displayName}
                        </h1>
                        <Badge
                            variant="outline"
                            className="rounded-full border-[#17A2B8] bg-[#E8F7F9] px-3 py-0.5 text-xs font-medium text-[#17A2B8]"
                        >
                            {displayRole}
                        </Badge>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1.5">
                            <UserIcon className="h-4 w-4" />
                            <span>{displayId}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <MailIcon className="h-4 w-4" />
                            <span>{displayEmail}</span>
                            {displayEmail !== 'N/A' && (
                                <button
                                    onClick={() =>
                                        handleCopy(displayEmail, 'email')
                                    }
                                    className="ml-1 text-muted-foreground/60 hover:text-muted-foreground transition-colors"
                                >
                                    {copiedField === 'email' ? (
                                        <Check className="h-3.5 w-3.5 text-green-500" />
                                    ) : (
                                        <Copy className="h-3.5 w-3.5" />
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-border">
                <div className="flex gap-8">
                    <button
                        onClick={() => setActiveTab('account')}
                        className={`relative pb-3 text-sm font-medium transition-colors ${
                            activeTab === 'account'
                                ? 'text-foreground'
                                : 'text-muted-foreground hover:text-foreground'
                        }`}
                    >
                        Account Information
                        {activeTab === 'account' && (
                            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#17A2B8] rounded-full" />
                        )}
                    </button>
                    <button
                        onClick={() => setActiveTab('security')}
                        className={`relative pb-3 text-sm font-medium transition-colors ${
                            activeTab === 'security'
                                ? 'text-foreground'
                                : 'text-muted-foreground hover:text-foreground'
                        }`}
                    >
                        Security
                        {activeTab === 'security' && (
                            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#17A2B8] rounded-full" />
                        )}
                    </button>
                </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'account' ? (
                <AccountInformationTab />
            ) : (
                <SecurityTab
                    onChangePassword={() => setIsPasswordModalOpen(true)}
                />
            )}

            {/* Change Password Modal */}
            <ChangePasswordModal
                open={isPasswordModalOpen}
                onOpenChange={setIsPasswordModalOpen}
            />
        </div>
    )
}

// Account Information Tab Component
const AccountInformationTab = () => {
    return (
        <div className="space-y-6">
            <h2 className="text-sm font-medium text-foreground">
                Personal Information
            </h2>

            <div className="rounded-xl bg-[#F8FAFC] p-6">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
                    <InfoItem label="Date Joined" value="N/A" />
                    <InfoItem label="Country of Residence" value="N/A" />
                    <InfoItem label="Residential Address" value="N/A" />
                    <InfoItem label="Date of birth" value="N/A" />
                    <InfoItem label="Gender" value="N/A" />
                </div>
            </div>
        </div>
    )
}

// Info Item Component
const InfoItem = ({
    label,
    value,
    className,
}: {
    label: string
    value: React.ReactNode
    className?: string
}) => {
    return (
        <div className="relative flex flex-col items-center text-center">
            <p className="text-xs text-muted-foreground mb-2">{label}</p>
            <p
                className={`text-sm font-medium text-foreground ${className ?? ''}`}
            >
                {value}
            </p>
            {/* Vertical divider - hidden on last item and on mobile */}
            <div className="absolute right-0 top-0 bottom-0 w-px bg-border hidden lg:block last:hidden" />
        </div>
    )
}

// Security Tab Component
const SecurityTab = ({
    onChangePassword,
}: {
    onChangePassword: () => void
}) => {
    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between ">
                <div className="space-y-1">
                    <h3 className="text-sm font-semibold text-foreground">
                        Change Password
                    </h3>
                    <p className="text-sm text-muted-foreground">
                        Update password for enhanced account security.
                    </p>
                </div>
                <Button
                    onClick={onChangePassword}
                    className="h-10 rounded-full bg-[#17A2B8] px-6 text-sm font-medium text-white hover:bg-[#138D9E]"
                >
                    Change password
                </Button>
            </div>
        </div>
    )
}

// Change Password Modal Component
const ChangePasswordModal = ({
    open,
    onOpenChange,
}: {
    open: boolean
    onOpenChange: (open: boolean) => void
}) => {
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [isLoading, setIsLoading] = useState(false)

    const requirements = useMemo(
        () => [
            {
                id: 'uppercase',
                label: 'At least 1 uppercase',
                met: /[A-Z]/.test(password),
            },
            {
                id: 'number',
                label: 'At least 1 number',
                met: /\d/.test(password),
            },
            {
                id: 'length',
                label: 'At least 8 characters',
                met: password.length >= 8,
            },
        ],
        [password]
    )

    const metCount = requirements.filter(req => req.met).length
    const strengthClass =
        metCount <= 1
            ? 'bg-[#EB4B4B]'
            : metCount === 2
              ? 'bg-[#F4B740]'
              : 'bg-[#0FA3B1]'
    const passwordsMatch =
        confirmPassword.length === 0 ? true : confirmPassword === password
    const canSubmit =
        requirements.every(req => req.met) &&
        passwordsMatch &&
        password.length > 0 &&
        confirmPassword.length > 0

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (!canSubmit) return

        setIsLoading(true)
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1500))
        setIsLoading(false)

        // Reset and close
        setPassword('')
        setConfirmPassword('')
        onOpenChange(false)
    }

    const handleOpenChange = (newOpen: boolean) => {
        if (!newOpen) {
            setPassword('')
            setConfirmPassword('')
        }
        onOpenChange(newOpen)
    }

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-[480px] rounded-2xl p-0 gap-0">
                <DialogHeader className="p-6 pb-0">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F8FAFC]">
                            <img
                                src={AccountSettingsIcon}
                                alt=""
                                className="h-5 w-5"
                            />
                        </div>
                        <div>
                            <DialogTitle className="text-lg font-semibold text-foreground">
                                Change password
                            </DialogTitle>
                            <DialogDescription className="text-sm text-muted-foreground">
                                Change your Ogaboss account password
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="p-6 space-y-5">
                    <div className="space-y-4">
                        <div>
                            <PasswordInput
                                placeholder="Enter password"
                                value={password}
                                className="h-12 rounded-xl bg-secondary border-0"
                                onChange={e => setPassword(e.target.value)}
                                required
                            />
                            <div className="mt-4">
                                <div className="flex gap-2">
                                    {[0, 1, 2].map(segment => (
                                        <span
                                            key={segment}
                                            className={`h-1 flex-1 rounded-full transition-colors ${
                                                segment < metCount
                                                    ? strengthClass
                                                    : 'bg-[#E6E9F2]'
                                            }`}
                                        />
                                    ))}
                                </div>
                                <p className="mt-3 text-sm font-medium text-foreground">
                                    Must contain at least;
                                </p>
                                <ul className="mt-2 space-y-2 text-sm text-muted-foreground">
                                    {requirements.map(requirement => (
                                        <li
                                            key={requirement.id}
                                            className="flex items-center gap-2"
                                        >
                                            {requirement.met ? (
                                                <CheckCircle2 className="h-4 w-4 text-[#0CB477]" />
                                            ) : (
                                                <XCircle className="h-4 w-4 text-[#A2AAB8]" />
                                            )}
                                            <span>{requirement.label}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        <PasswordInput
                            placeholder="Confirm password"
                            value={confirmPassword}
                            className="h-12 rounded-xl bg-secondary border-0"
                            onChange={e => setConfirmPassword(e.target.value)}
                            required
                            aria-invalid={!passwordsMatch}
                        />
                        {!passwordsMatch && (
                            <p className="text-sm text-destructive">
                                Passwords do not match.
                            </p>
                        )}
                    </div>

                    <DialogFooter className="flex-row gap-3 pt-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => handleOpenChange(false)}
                            className="h-12 flex-1 rounded-full border-border text-sm font-medium"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={isLoading || !canSubmit}
                            className="h-12 flex-1 rounded-full bg-[#17A2B8] text-sm font-medium text-white hover:bg-[#138D9E] disabled:bg-[#9EDBE1] disabled:text-white"
                        >
                            {isLoading ? 'Saving...' : 'Save'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}

// Icon Components
const UserIcon = ({ className }: { className?: string }) => (
    <svg
        className={className}
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M8 8C9.65685 8 11 6.65685 11 5C11 3.34315 9.65685 2 8 2C6.34315 2 5 3.34315 5 5C5 6.65685 6.34315 8 8 8Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M13.7273 14C13.7273 11.5817 11.1625 9.63636 8 9.63636C4.83753 9.63636 2.27273 11.5817 2.27273 14"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
)

const MailIcon = ({ className }: { className?: string }) => (
    <svg
        className={className}
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <rect
            x="1.5"
            y="3"
            width="13"
            height="10"
            rx="2"
            stroke="currentColor"
            strokeWidth="1.5"
        />
        <path
            d="M1.5 5L8 9L14.5 5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
)

export default AccountSettings
